import { useCallback } from 'react'

import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { useNotifications } from '@/shared/components/Notification'
import { ApiException } from '@/shared/utils/api'

import { workspacesService } from '../services/workspaces.service'

export const WORKSPACES_QUERY_KEY = ['workspaces'] as const

/** Workspaces the session can enter; `enabled` is false for anonymous visitors. */
export function useWorkspaces(enabled = true) {
  return useQuery({
    queryKey: WORKSPACES_QUERY_KEY,
    queryFn: () => workspacesService.list(),
    enabled,
    staleTime: 60_000,
    retry: false,
  })
}

/**
 * Enter a workspace: the service swaps the token, then the whole app reloads
 * from "/" so no cached query keeps another tenant's data.
 */
export function useEnterWorkspace() {
  const { t } = useTranslation('common')
  const { showError } = useNotifications()

  const mutation = useMutation({
    mutationFn: (tenantId: string) => workspacesService.enter(tenantId),
    onSuccess: () => {
      window.location.assign('/')
    },
    onError: (error: unknown) => {
      const code = error instanceof ApiException ? error.code : undefined
      showError(
        code === 'WORKSPACE_NOT_GRANTED'
          ? t('workspaces.errors.WORKSPACE_NOT_GRANTED')
          : t('workspaces.errors.generic'),
      )
    },
  })

  const enter = useCallback((tenantId: string) => mutation.mutate(tenantId), [mutation])
  return { enter, isEntering: mutation.isPending }
}
